import type { Monitor } from '@/types/api';

/**
 * The host a monitor is watching, however its target happens to be written.
 *
 * ## Why this exists
 *
 * A customer does not think in monitors, they think in sites. One domain routinely has four checks
 * against it — the site responds, the certificate is valid, the DNS record resolves, the
 * registration has not lapsed — and each is stored with a target in its own format:
 *
 *     https://example.com/     an HTTP check
 *     example.com:443          a TCP or SSL check
 *     example.com              a DNS or expiry check
 *
 * Left as they are, those four sit in a flat list looking unrelated. Reduced to `example.com` they
 * become one thing with four opinions about it, which is the question actually being asked.
 *
 * Anything unparseable is returned trimmed rather than dropped. A monitor with an odd target is
 * still a monitor, and hiding it from its own page would be worse than grouping it oddly.
 */
export function hostOf(target: string): string {
  const value = target.trim();

  if (value === '') {
    return 'Other';
  }

  // A URL, when it is one. `URL` handles ports, paths, credentials and IPv6 brackets correctly,
  // and doing that by hand is where this kind of helper usually goes wrong.
  if (value.includes('://')) {
    try {
      return normalise(new URL(value).hostname);
    } catch {
      // Fall through: a malformed URL is still worth grouping by whatever it starts with.
    }
  }

  // `host:port`, but not a bare IPv6 address, which is full of colons and has no port here.
  const withoutPort =
    value.includes(':') && value.split(':').length === 2 ? value.split(':')[0] : value;

  return normalise(withoutPort);
}

/**
 * Both branches must reduce the same site to the same key.
 *
 * `www.` is stripped here rather than in one branch, because an HTTP check on
 * `https://www.example.com/` and a DNS check on `example.com` are watching one site and belong
 * under one heading — and stripping it in only the bare-host path would have split them.
 */
function normalise(host: string): string {
  return host.replace(/^www\./i, '').toLowerCase();
}

export interface MonitorGroup {
  host: string;
  monitors: Monitor[];
  /** The worst thing true of any monitor in the group — what the header should say. */
  status: 'down' | 'stale' | 'pending' | 'up' | 'paused';
}

/**
 * Groups monitors by host, worst group first.
 *
 * Ordering is by severity rather than alphabetically because the reason somebody opens this page is
 * usually that something is wrong, and an alphabetical list puts `zeta.com` last on the day it is
 * the only thing down.
 */
export function groupByHost(monitors: Monitor[]): MonitorGroup[] {
  const groups = new Map<string, Monitor[]>();

  for (const monitor of monitors) {
    const host = hostOf(monitor.target);
    const existing = groups.get(host);

    if (existing === undefined) {
      groups.set(host, [monitor]);
    } else {
      existing.push(monitor);
    }
  }

  return [...groups.entries()]
    .map(([host, list]) => ({ host, monitors: list, status: worstOf(list) }))
    .sort((a, b) => severity(a.status) - severity(b.status) || a.host.localeCompare(b.host));
}

/**
 * The group's headline status.
 *
 * Stale outranks up deliberately: a reading the platform has not refreshed is not good news, and a
 * group header saying "Up" over a monitor last checked four hours ago is the single misstatement
 * that makes monitoring worse than having none.
 */
function worstOf(monitors: Monitor[]): MonitorGroup['status'] {
  const live = monitors.filter((m) => m.status !== 'paused');

  if (live.length === 0) return 'paused';
  if (live.some((m) => !m.is_stale && m.status === 'down')) return 'down';
  if (live.some((m) => m.is_stale)) return 'stale';
  if (live.some((m) => m.status === 'pending')) return 'pending';

  return 'up';
}

function severity(status: MonitorGroup['status']): number {
  return { down: 0, stale: 1, pending: 2, up: 3, paused: 4 }[status];
}

/** Free-text match across the fields somebody would actually type. */
export function matchesSearch(monitor: Monitor, term: string): boolean {
  const needle = term.trim().toLowerCase();

  if (needle === '') {
    return true;
  }

  return (
    monitor.name.toLowerCase().includes(needle) ||
    monitor.target.toLowerCase().includes(needle) ||
    monitor.kind_label.toLowerCase().includes(needle) ||
    hostOf(monitor.target).includes(needle)
  );
}
